/**
 * アプリケーションのルートコンポーネント。
 *
 * - 認証状態に応じて Login / SignUp / メインページを切り替える。
 * - 未認証時は `/signup` でセルフサインアップが可能、それ以外は `/login` に集約される。
 * - 認証済みの場合は `/login` `/signup` を踏んでもメインページにリダイレクトする。
 * - ヘッダーのタイトルはトップページ (線区選択画面) へ戻るリンクとして機能する。
 */
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router';

import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { SignUpPage } from './auth/SignUpPage';
import type { AppConfig } from './config';
import { APP_TITLE } from './constants/appInfo';
import {
  HomeNavigationGuardProvider,
  useHomeNavigationGuard,
} from './contexts/HomeNavigationGuard';
import { ChimeMasterScreen } from './components/ChimeMasterScreen';
import { clearStoredSelection } from './hooks/useRouteSelection';
import { AlertRecipientPage } from './pages/AlertRecipientPage';
import { BroadcastPointsPage } from './pages/BroadcastPointsPage';
import { DeviceRegistrationListPage } from './pages/DeviceRegistrationListPage';

interface AppProps {
  config: AppConfig;
}

export default function App({ config }: AppProps): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-muted)' }}>
        セッションを確認中...
      </div>
    );
  }

  if (!isAuthenticated) {
    // 未認証: ログイン / サインアップのみ表示する
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // `.app-shell` は「ヘッダー固定 + メインが残り全部」の縦フレックス。
  // これによりメイン領域 (地図 / 一覧) がビューポート高さいっぱいまで広がる。
  return (
    // HomeNavigationGuardProvider: 編集モーダル表示中はヘッダーからのトップ遷移を
    // 無効化するため、ヘッダーとページで状態を共有する。
    <HomeNavigationGuardProvider>
      <div className="app-shell">
        <AppHeader />
        <main className="app-main">
          <Routes>
            <Route path="/" element={<BroadcastPointsPage config={config} />} />
            <Route path="/devices" element={<DeviceRegistrationListPage config={config} />} />
            <Route path="/alert-recipients" element={<AlertRecipientPage config={config} />} />
            {/* 要件 3・4: 放送開始 / 終了チャイム音マスタ (ChimeMaster) の管理画面 */}
            <Route path="/chimes" element={<ChimeMasterScreen config={config} />} />
            {/* 認証済みでログイン/サインアップへ来た場合はトップへリダイレクト */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </HomeNavigationGuardProvider>
  );
}

function AppHeader(): React.JSX.Element {
  const { username, signOut } = useAuth();
  const navigate = useNavigate();
  const { blocked } = useHomeNavigationGuard();

  /**
   * ヘッダータイトルクリック: トップページ (線区選択画面) に戻る。
   *
   * - 線区/方面の選択状態は URL クエリと sessionStorage に保持されているため、
   *   両方をクリアしてから `/` へ遷移する。
   * - 編集モーダル表示中 (`blocked`) は誤操作で入力内容を失わないよう無効化する。
   */
  const handleHomeClick = (): void => {
    if (blocked) return;
    clearStoredSelection();
    navigate('/');
  };

  return (
    <header className="app-header">
      <h1>
        <button
          type="button"
          className="app-header__home"
          data-testid="app-header-home"
          onClick={handleHomeClick}
          disabled={blocked}
          title={
            blocked
              ? '編集中はトップページに戻れません (先に保存またはキャンセルしてください)'
              : 'トップページ (線区選択) に戻る'
          }
        >
          {APP_TITLE}
        </button>
      </h1>

      {/*
        ヘッダー右側: ページ切替ナビ + ユーザー情報 をまとめて右寄せする。
        3 ブロックを space-between で並べるとナビだけが画面中央に浮いてしまい、
        どこに属する要素か分かりにくいため、右側でグループ化する。
      */}
      <div className="app-header__right">
        <nav className="app-nav" aria-label="ページ切替">
          <NavLink
            to="/"
            end
            onClick={(e) => {
              if (blocked) e.preventDefault();
            }}
            aria-disabled={blocked}
            tabIndex={blocked ? -1 : undefined}
            className={({ isActive }) => `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`}
            data-testid="nav-home"
          >
            放送地点マスタ
          </NavLink>
          <NavLink
            to="/devices"
            onClick={(e) => {
              if (blocked) e.preventDefault();
            }}
            aria-disabled={blocked}
            tabIndex={blocked ? -1 : undefined}
            className={({ isActive }) => `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`}
            data-testid="nav-devices"
          >
            端末管理
          </NavLink>
          <NavLink
            to="/alert-recipients"
            onClick={(e) => {
              if (blocked) e.preventDefault();
            }}
            aria-disabled={blocked}
            tabIndex={blocked ? -1 : undefined}
            className={({ isActive }) => `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`}
            data-testid="nav-alert-recipients"
          >
            通知先メール管理
          </NavLink>
          {/* 要件 3・4: チャイム音マスタ管理画面へのナビゲーション */}
          <NavLink
            to="/chimes"
            onClick={(e) => {
              if (blocked) e.preventDefault();
            }}
            aria-disabled={blocked}
            tabIndex={blocked ? -1 : undefined}
            className={({ isActive }) => `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`}
            data-testid="nav-chimes"
          >
            チャイム音マスタ
          </NavLink>
        </nav>

        <div className="user-info">
          <span>{username ?? ''}</span>
          <button
            type="button"
            onClick={() => {
              signOut();
              clearStoredSelection();
              navigate('/');
            }}
          >
            ログアウト
          </button>
        </div>
      </div>
    </header>
  );
}
